import { baseApi } from './baseApi';
import { 
  Area, 
  Table, 
  CreateTableRequest, 
  UpdateTableRequest, 
  UpdateTableStatusRequest,
  OpenTableRequest 
} from '../types/tables';

export const tablesApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Areas endpoints
    getAreas: builder.query<Area[], void>({
      query: () => '/areas',
      providesTags: ['Area'],
    }),
    getArea: builder.query<Area, string>({
      query: (id) => `/areas/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'Area', id }],
    }),
    createArea: builder.mutation<Area, { name: string; description?: string }>({
      query: (newArea) => ({
        url: '/areas',
        method: 'POST',
        body: newArea,
      }),
      invalidatesTags: ['Area'],
    }),
    updateArea: builder.mutation<Area, { id: string; name: string; description?: string }>({
      query: ({ id, ...patch }) => ({
        url: `/areas/${id}`,
        method: 'PUT',
        body: patch,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Area', id },
        'Area' // Invalidate list cache to refresh all queries
      ],
    }),
    deleteArea: builder.mutation<void, string>({
      query: (id) => ({
        url: `/areas/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Area'],
    }),

    // Tables endpoints
    getTables: builder.query<Table[], void>({
      query: () => '/tables',
      providesTags: ['Table'],
    }),
    getTablesByArea: builder.query<Table[], string>({
      query: (areaId) => `/tables/area/${areaId}`,
      providesTags: ['Table'],
    }),
    getTable: builder.query<Table, string>({
      query: (id) => `/tables/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'Table', id }],
    }),
    createTable: builder.mutation<Table, CreateTableRequest>({
      query: (newTable) => ({
        url: '/tables',
        method: 'POST',
        body: newTable,
      }),
      invalidatesTags: ['Table'],
    }),
    updateTable: builder.mutation<Table, { id: string } & UpdateTableRequest>({
      query: ({ id, ...patch }) => ({
        url: `/tables/${id}`,
        method: 'PUT',
        body: patch,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Table', id },
        'Table' // Invalidate list cache to refresh all queries
      ],
    }),
    updateTableStatus: builder.mutation<void, { id: string; status: UpdateTableStatusRequest }>({
      query: ({ id, status }) => ({
        url: `/tables/${id}/status`,
        method: 'PATCH',
        body: status,
      }),
      invalidatesTags: ['Table'],
    }),
    deleteTable: builder.mutation<void, string>({
      query: (id) => ({
        url: `/tables/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Table'],
    }),
    openTable: builder.mutation<void, OpenTableRequest>({
      query: (openTableData) => ({
        url: '/tables/open',
        method: 'POST',
        body: openTableData,
      }),
      // Opening a table creates a new Order — invalidate 'Order' so any
      // orders list/grid in this client picks it up. Without this, the
      // Orders view could miss the newly opened order until next refetch.
      invalidatesTags: ['Table', 'Order'],
    }),
    closeTable: builder.mutation<void, string>({
      query: (id) => ({
        url: `/tables/${id}/close`,
        method: 'POST',
      }),
      // Closing a table closes its Open order(s) — invalidate 'Order' so
      // the Orders view reflects the new Closed status.
      invalidatesTags: ['Table', 'Order'],
    }),
    transferTable: builder.mutation<void, { fromTableId: string; toTableId: string }>({
      query: ({ fromTableId, toTableId }) => ({
        url: `/tables/${fromTableId}/transfer/${toTableId}`,
        method: 'POST',
      }),
      // Transfer reassigns Order.TableId — Orders view shows the new table.
      invalidatesTags: ['Table', 'Order'],
    }),
    mergeTables: builder.mutation<void, { primaryTableId: string; secondaryTableIds: string[] }>({
      query: ({ primaryTableId, secondaryTableIds }) => ({
        url: `/tables/${primaryTableId}/merge`,
        method: 'POST',
        body: { secondaryTableIds },
      }),
      invalidatesTags: ['Table'],
    }),
    splitTables: builder.mutation<void, string>({
      query: (primaryTableId) => ({
        url: `/tables/${primaryTableId}/split`,
        method: 'POST',
      }),
      invalidatesTags: ['Table'],
    }),
  }),
});

export const {
  // Areas hooks
  useGetAreasQuery,
  useGetAreaQuery,
  useCreateAreaMutation,
  useUpdateAreaMutation,
  useDeleteAreaMutation,
  
  // Tables hooks
  useGetTablesQuery,
  useGetTablesByAreaQuery,
  useGetTableQuery,
  useCreateTableMutation,
  useUpdateTableMutation,
  useUpdateTableStatusMutation,
  useDeleteTableMutation,
  useOpenTableMutation,
  useCloseTableMutation,
  useTransferTableMutation,
  useMergeTablesMutation,
  useSplitTablesMutation,
} = tablesApi;